import { useMemo } from "react";

import { CloudFlowNodeType } from "@doitintl/cmp-models";
import { type Node } from "@xyflow/react";

import { type RFNode } from "../../../types";
import { type CloudFlowStatus } from "../type";

type GetCloudFlowStatusParams = {
  isInitialState: boolean;
  hasErrors: boolean;
  isComplete: boolean;
};

const getCloudFlowStatus = ({ isInitialState, hasErrors, isComplete }: GetCloudFlowStatusParams): CloudFlowStatus => {
  if (isInitialState) return "initial";
  if (hasErrors) return "incomplete";
  if (isComplete) return "readyToPublish";
  return "incomplete";
};

export const useCloudFlowStatus = (nodes: Node<RFNode<CloudFlowNodeType>>[]) => {
  const nodesWithErrors = useMemo(
    () =>
      nodes
        .filter(
          (node) =>
            Object.values(node.data.nodeData.errorMessages || {}).filter(Boolean).length > 0 ||
            node.type === CloudFlowNodeType.START_STEP
        )
        .sort((a, b) => {
          const stepA = a.data.stepNumber || 0;
          const stepB = b.data.stepNumber || 0;
          return stepA - stepB;
        }),
    [nodes]
  );

  const hasActionNode = nodes.some((node) => node.type === CloudFlowNodeType.ACTION);

  let hasTriggerError = false;
  let hasDateTimeNodeError = false;
  for (const node of nodesWithErrors) {
    if (node.type === CloudFlowNodeType.TRIGGER) {
      hasTriggerError = true;
      break;
    }
    if (node.type === CloudFlowNodeType.DATETIME_TRANSFORM) {
      hasDateTimeNodeError = true;
      break;
    }
  }

  const isInitialState = !hasTriggerError && !hasDateTimeNodeError && !hasActionNode;
  const isComplete = nodesWithErrors.length === 0 && hasActionNode;

  const status = getCloudFlowStatus({
    isInitialState,
    hasErrors: nodesWithErrors.length > 0,
    isComplete,
  });

  return {
    status,
    nodesWithErrors,
  };
};
